import React, { useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Button, notification, Skeleton } from 'antd'
import { Flex, Header, Icon, Text } from 'govwell-ui'
import { AicaEmailDigestFrequency } from 'types/graphql'

import {
  useCreateAicaEmailDigestSubscriptionMutation,
  useDeleteAicaEmailDigestSubscriptionMutation,
  useGetAicaEmailDigestSubscriptionsQuery,
  useSendTestAicaDigestMutation,
  useUpdateAicaEmailDigestSubscriptionMutation,
} from 'src/fetch/aicaEmailDigestSubscriptions'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey } from 'src/utils/queryClient'

import AddDigestSubscriberModal from './AddDigestSubscriberModal'
import EmailInsightsTable from './EmailInsightsTable'
import SendTestDigestModal from './SendTestDigestModal'

const EmailInsightsTab = () => {
  const queryClient = useQueryClient()
  const organizationId = useMyOrgId()

  const { data: subscriptions, isLoading } = useGetAicaEmailDigestSubscriptionsQuery()
  const { mutateAsync: createSubscription, isPending: isCreating } =
    useCreateAicaEmailDigestSubscriptionMutation()
  const { mutateAsync: updateSubscription, isPending: isUpdating } =
    useUpdateAicaEmailDigestSubscriptionMutation()
  const { mutateAsync: deleteSubscription, isPending: isDeleting } =
    useDeleteAicaEmailDigestSubscriptionMutation()
  const { mutateAsync: sendTestDigest, isPending: isSendingTest } = useSendTestAicaDigestMutation()

  const addSubscriberModalState = useDisclosure()
  const sendTestModalState = useDisclosure()

  const invalidateSubscriptions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.AicaEmailDigestSubscriptions] })
  }, [queryClient])

  const handleAddSubscriber = useCallback(
    async (userId: number, frequency: AicaEmailDigestFrequency) => {
      await createSubscription({ input: { userId, frequency, organizationId } })
      invalidateSubscriptions()
      addSubscriberModalState.close()
    },
    [createSubscription, organizationId, invalidateSubscriptions, addSubscriberModalState]
  )

  const handleUpdateFrequency = useCallback(
    async (id: number, frequency: AicaEmailDigestFrequency) => {
      await updateSubscription({ id, input: { frequency } })
      invalidateSubscriptions()
    },
    [updateSubscription, invalidateSubscriptions]
  )

  const handleRemoveSubscriber = useCallback(
    async (id: number) => {
      await deleteSubscription({ input: { id } })
      invalidateSubscriptions()
    },
    [deleteSubscription, invalidateSubscriptions]
  )

  const handleSendTestDigest = useCallback(
    async (emailAddress: string, frequency: AicaEmailDigestFrequency) => {
      try {
        await sendTestDigest({ input: { emailAddress, frequency, organizationId } })
        sendTestModalState.close()
        notification.success({
          message: 'Test digest sent',
          description: `A test ${frequency.toLowerCase()} digest has been sent to ${emailAddress}.`,
        })
      } catch {
        notification.error({
          message: 'Failed to send test digest',
          description: 'Please check the email address and try again.',
        })
      }
    },
    [sendTestDigest, organizationId, sendTestModalState]
  )

  const stableSubscriptions = useMemo(() => subscriptions ?? [], [subscriptions])

  const existingUserIds = useMemo(
    () => stableSubscriptions.map((sub) => sub.user.id),
    [stableSubscriptions]
  )

  const isProcessing = isCreating || isUpdating || isDeleting

  return (
    <>
      <Flex direction="column" gap={4} align="stretch">
        <Flex direction="column" gap={2} align="stretch">
          <Header
            title="Email Insights"
            subtitle="Configure automated email digests to push AICA value metrics to stakeholders without requiring them to log into GovWell."
          />
          <Text.Body>
            Subscribers will receive regular email digests containing key metrics including total
            questions answered, top topics, after-hours usage, and sample Q&A pairs. Stakeholders
            can receive value proof without any action required.
          </Text.Body>
        </Flex>

        <Flex direction="column" gap={2} align="stretch">
          <Header
            title="Digest Subscribers"
            actions={
              <>
                <Button
                  icon={<Icon.FaEnvelope />}
                  onClick={sendTestModalState.open}
                  disabled={isProcessing || isSendingTest}
                >
                  Send test digest
                </Button>
                <Button
                  icon={<Icon.FaPlus />}
                  type="primary"
                  onClick={addSubscriberModalState.open}
                  disabled={isProcessing}
                >
                  Add subscriber
                </Button>
              </>
            }
          />

          {isLoading ? (
            <Skeleton active />
          ) : (
            <EmailInsightsTable
              subscriptions={stableSubscriptions}
              onUpdateFrequency={handleUpdateFrequency}
              onRemove={handleRemoveSubscriber}
              isProcessing={isProcessing}
            />
          )}
        </Flex>

        <Flex direction="column" gap={2} align="stretch">
          <Header title="Digest Schedule" />
          <Text.Body>
            <strong>Weekly digests</strong> are sent every Monday at 9 AM (your timezone) and
            include metrics from the previous week.
          </Text.Body>
          <Text.Body>
            <strong>Monthly digests</strong> are sent on the 1st of each month at 9 AM (your
            timezone) and include metrics from the previous month.
          </Text.Body>
        </Flex>
      </Flex>

      {addSubscriberModalState.isOpen && (
        <AddDigestSubscriberModal
          onClose={addSubscriberModalState.close}
          onAdd={handleAddSubscriber}
          existingUserIds={existingUserIds}
          isLoading={isCreating}
        />
      )}

      {sendTestModalState.isOpen && (
        <SendTestDigestModal
          onClose={sendTestModalState.close}
          onSend={handleSendTestDigest}
          isLoading={isSendingTest}
        />
      )}
    </>
  )
}

export default React.memo(EmailInsightsTab)
